import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames/bind';
import { useIntl } from 'umi';
import {
  useDebounceFn,
  useHover,
  useMemoizedFn,
  useThrottle,
  useThrottleEffect,
  useThrottleFn,
} from 'ahooks';
import { ListRange, Virtuoso } from 'react-virtuoso';
import Icon from '@/components/Icon';
import Style from './index.less';
import RecordItem from '@/components/RecordList/RecordItem';
import { RecordMessage } from '@/types/record';
import TranscribeItem from './TranscribeItem';

export function useRecordListData() {
  const recordList = useRef<RecordMessage[]>([]);
  const [moreLoading, setMoreLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [updateTag, setUpdateTag] = useState(1);
  const [nowUpdateTag, setNowUpdateTag] = useState(1);
  const tempMap: React.MutableRefObject<Record<string, RecordMessage>> = useRef({});
  const [listKey, setListKey] = useState(new Date().getTime());
  const updateTagT = useThrottle(updateTag, { wait: 180 });

  const pushRecord = useCallback((record: RecordMessage) => {
    const uid = record.yxuid || record.uid;
    if (tempMap.current[uid]?.clearTimer) window.clearTimeout(tempMap.current[uid]?.clearTimer);
    if (record.sentenceType === 2) {
      tempMap.current[uid] = {
        ...record,
        id: record.yxuid,
        clearTimer: window.setTimeout(() => {
          if (tempMap.current[uid]) {
            delete tempMap.current[uid];
            setUpdateTag((n) => n + 1);
          }
        }, 5000), // 5s后没有后续状态就清除
      };
      setUpdateTag((n) => n + 1);
    } else {
      delete tempMap.current[uid];
      recordList.current.push(record);
      setUpdateTag((n) => n + 1);
    }
  }, []);

  const clearRecord = useCallback((all: boolean) => {
    if (all) {
      setHasMore(true);
      recordList.current = [];
      setListKey(new Date().getTime());
    }
    tempMap.current = {};
    setNowUpdateTag((n) => n + 1);
  }, []);

  const loadHistoryRecord = useCallback((list) => {
    recordList.current = [...list, ...recordList.current];
    setNowUpdateTag((n) => n + 1);
  }, []);

  const resultList = useMemo((): RecordMessage[] => {
    return [...recordList.current, ...Object.values(tempMap.current)];
  }, [updateTagT, nowUpdateTag]);

  // 压缩列表长度
  const reduceList = useCallback((targetLength, maxLength) => {
    if (recordList.current.length > maxLength) {
      recordList.current = recordList.current.slice(-targetLength);
      setHasMore(true);
      setNowUpdateTag((n) => n + 1);
    }
  }, []);

  return {
    recordList: resultList,
    moreLoading,
    setMoreLoading,
    hasMore,
    setHasMore,
    pushRecord,
    loadHistoryRecord,
    clearRecord,
    listKey,
    reduceList,
  };
}

const $c = classnames.bind(Style);

interface ChatPanelProps {
  list: any[];
  hasMore: boolean;
  loadMore: (T: any) => any;
  meetingInfo?: defs.client.BriefInformationOfTheMeeting | null;
  onScrollToBottom?: (isBootom: boolean) => any; // 是否滑到了底部
  isWebTranscribe?: boolean;
}

const RecordList: React.FC<ChatPanelProps> = function (props: ChatPanelProps) {
  const { list, loadMore, hasMore, meetingInfo, onScrollToBottom, cRef, isWebTranscribe } = props;
  const [isScrolling, setScrolling] = useState<boolean>(false);
  const [isToBottom, setIsToBottom] = useState<boolean>(false);
  const [listRange, setListRange] = useState<ListRange>({ startIndex: 10, endIndex: 10 });
  const oldListLength = useRef(0);
  const { formatMessage } = useIntl();
  const loadMoreIsLoading = useRef(false);
  const inited = useRef(false);
  const scrollDom = React.useRef<Virtuoso>(null);

  const $i = (mid: string, data: Record<string, string>) => {
    return formatMessage({ id: mid }, data || {});
  };

  const loadMoreList = useMemoizedFn(() => {
    if (!loadMoreIsLoading.current && hasMore) {
      loadMoreIsLoading.current = true;
      console.log(list[0]);
      const loadPromise = Promise.resolve(loadMore?.());
      loadPromise.finally(() => {
        loadMoreIsLoading.current = false;
      });
      return loadPromise;
    }
    return Promise.resolve('');
  });

  useThrottleEffect(
    () => {
      oldListLength.current = list.length;
    },
    [list.length],
    { wait: 20, leading: false }
  );

  const loadingRef = useRef(null);
  const listRef = useRef(null);
  const isHovering = useHover(listRef);

  useEffect(() => {
    if (listRange.startIndex < 2 && inited.current) {
      loadMoreList();
    }
  }, [listRange]);

  // 保持滚到顶部加载位置
  const holdScrollTop = useMemoizedFn(() => {
    if (isScrolling) {
      scrollDom.current?.scrollToIndex({
        index: list.length - oldListLength.current,
        behavior: 'auto',
        align: 'start',
      });
    }
  });

  // 第一条消息变化时
  useEffect(() => {
    holdScrollTop();
  }, [list?.[0]]);

  const { run: callBackOnScrollToBottom } = useThrottleFn(
    () => {
      onScrollToBottom?.(!isScrolling);
    },
    { wait: 1000 }
  );

  const scrollPopoToBottom = useMemoizedFn((behavior: 'smooth' | 'auto' = 'auto') => {
    scrollDom.current?.scrollToIndex({ index: list.length - 1, behavior });
    callBackOnScrollToBottom();
  });

  useThrottleEffect(
    () => {
      if (isToBottom || !isScrolling) {
        scrollPopoToBottom();
      }
    },
    [isToBottom, isScrolling, list],
    { wait: 10 }
  );

  const { run: updateIsScrolling } = useDebounceFn(
    (isScolling) => {
      setScrolling(isScolling);
    },
    { wait: 100 }
  );

  React.useEffect(() => {
    // 如果列表重置则滚到底
    if (!list.length) {
      scrollPopoToBottom();
    }
  }, [list]);

  useImperativeHandle(cRef, () => {
    return {
      scrollToBottom: scrollPopoToBottom,
    };
  });

  useEffect(() => {
    loadMoreList().then(() => {
      scrollPopoToBottom('auto');
      setTimeout(() => {
        inited.current = true;
      }, 100);
    });
  }, []);

  const itemFn = useMemoizedFn((index, msg) => {
    if (isWebTranscribe) {
      return (
        <TranscribeItem
          msg={msg}
          preMsg={list[index - 1]}
          className={$c({ 'last-child': !list[index + 1] })}
        />
      );
    }
    return (
      <RecordItem
        msg={msg}
        preMsg={list[index - 1]}
        className={$c({ 'last-child': !list[index + 1] })}
      />
    );
  });

  return (
    <div className={$c('list')} ref={listRef}>
      <Virtuoso
        ref={scrollDom}
        data={list}
        itemContent={itemFn}
        // totalCount={list.length}
        style={{ height: '100%' }}
        className={$c('scroll-wrapper')}
        rangeChanged={setListRange}
        atBottomStateChange={(bottom) => {
          updateIsScrolling(isHovering && !bottom);
        }}
        components={{
          Header: () => (
            <div>
              <div className={$c('loading-box', { hide: !hasMore })} ref={loadingRef}>
                <Icon
                  name="icon-loading_w_nor"
                  className={$c('loading-icon', { loading: loadMoreIsLoading.current })}
                />
              </div>
              <div
                className={$c('loading-box', 'title-item', {
                  hide: !(!hasMore && list.length > 30),
                })}
              >
                {$i('TRANSCRIPTION_IS_ENABLED', { meetingName: meetingInfo?.meetingName || '' })}
              </div>
            </div>
          ),
        }}
      />
      <div
        className={$c('to-bottom-btn', { hide: !isScrolling })}
        onClick={() => scrollPopoToBottom('auto')}
      >
        <Icon name="icon-arrow_striaght_up_1" className={$c('icon')} />
      </div>
    </div>
  );
};

export default RecordList;
